import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useState, useMemo } from 'react';

import { useRouter } from 'src/routes/hooks';

import { useAuthContext } from 'src/auth/hooks';

import { varHover } from 'src/components/animate';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import GoogleSyncDialog from 'src/sections/googleSync/view/google-sync-dialog'
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Inicio',
    linkTo: '/',
  },
  {
    label: 'Cambiar contraseña',
    linkTo: '/dashboard/publicas',
  },
  {
    label: 'Syncronizar Google',
    linkTo: 'google'
  }
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();

  const { logout, user: dataUser } = useAuthContext();
  const syncGoogle = useMemo(()=> dataUser?.has_google_token, [dataUser])
  const popover = usePopover();
  const [openGoogleDialog, setOpenGoogleDialog] = useState(false)

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace('/');
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickItem = (path) => {
    popover.onClose();
    if(path === 'google'){
      if(syncGoogle){
        enqueueSnackbar('tu cuenta de Google ya se encuentra sincronizada', { variant: 'info' });
      }else{
        window.open(
          `${process.env.REACT_APP_HOST_API}/googlecalendar/google-sync`,
          '_blank',
          'noopener,noreferrer'
        );

      }
    }else{
      router.push(path);
    }
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={dataUser?.photoURL}
          alt={dataUser?.nombre}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {dataUser?.nombre?.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 250, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {dataUser?.nombre}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {dataUser?.correo}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {dataUser?.nombreEmpresa}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => {
            const isGoogle = option.linkTo === 'google';
            return(
              <MenuItem
                key={option.label}
                onClick={() => handleClickItem(option.linkTo)}
                sx={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <span>
                  {isGoogle && syncGoogle 
                    ? 'Google conectado' 
                    : option.label}
                </span>

                {isGoogle && (
                  <Iconify
                    icon={syncGoogle ? 'mdi:check-circle' : 'mdi:close-circle'}
                    sx={{
                      color: syncGoogle ? 'success.main' : 'error.main',
                      fontSize: 20,
                    }}
                  />
                )}
              </MenuItem>
            )
          }
        )}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Cerrar sesión
        </MenuItem>
      </CustomPopover>
    </>
  );
}
